/**
 * The code step of the sign-in page, kept for this tab (QA 2026-10-28, brk-6). A reload or Back
 * on "Enter the 6-digit code" went back to an empty email form, and each new try sent a new code:
 * five tries in an hour locked the address out for 60 minutes. Now the page comes back to the code
 * step with the address, for the life of the code. `sessionStorage`: one tab, gone with the tab.
 */
export const AUTH_CODE_STEP_STORAGE_KEY = 'm24AuthCodeStep';

/** The page offers a new code a minute after the last one (music24-api `LOGIN_CODE_REUSE_SECONDS`). */
export const RESEND_WAIT_SECONDS = 60;

/** The life of an emailed code (music24-api `LOGIN_CODE_TTL_MINUTES`). */
export const AUTH_CODE_LIFE_MS = 15 * 60 * 1000;

export interface StoredCodeStep {
  email: string;
  /** When the code went out (ms). The resend wait counts from it. */
  sentAt: number;
}

export function readStoredCodeStep(now = Date.now()): StoredCodeStep | null {
  try {
    const raw = sessionStorage.getItem(AUTH_CODE_STEP_STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const value = JSON.parse(raw) as Partial<StoredCodeStep> | null;
    if (typeof value?.email !== 'string' || value.email === '' || typeof value.sentAt !== 'number') {
      return null;
    }
    return now - value.sentAt < AUTH_CODE_LIFE_MS ? { email: value.email, sentAt: value.sentAt } : null;
  } catch (error) {
    console.warn('Could not read the code step of the sign-in page', error);
    return null;
  }
}

export function storeCodeStep(step: StoredCodeStep | null): void {
  try {
    if (step === null) {
      sessionStorage.removeItem(AUTH_CODE_STEP_STORAGE_KEY);
    } else {
      sessionStorage.setItem(AUTH_CODE_STEP_STORAGE_KEY, JSON.stringify(step));
    }
  } catch (error) {
    console.warn('Could not keep the code step of the sign-in page', error);
  }
}
